import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Link } from 'react-router-dom'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Card } from '@magnetic/card'
import { Numeral } from '@magnetic/numeral'
import { Flex } from '@magnetic/flex'
import { Tag } from '@magnetic/tag'
import { ProgressBar } from '@magnetic/progress-bar'
import { Row, Col } from '@magnetic/grid'
import { Skeleton } from '@magnetic/skeleton'
import {
  Folder,
  Package,
  Warning,
  ArrowsClockwise,
  Activity,
  ArrowRight,
  // Magnetic Status Icons
  Positive,
} from '@/lib/icons'
import { getOperatorMetrics, listProjects } from '@/lib/api'
import { useAuth } from '@/lib/use-auth'


export function DashboardPage() {
  const { user } = useAuth()
  const isSuperAdmin = user?.isSuperAdmin || false

  const { data: metricsResponse, isLoading: metricsLoading, error: metricsError } = useConnectQuery(
    getOperatorMetrics,
    {},
    {
      refetchInterval: 30000,
      enabled: isSuperAdmin,
    }
  )

  const { data: projectsResponse, isLoading: projectsLoading } = useConnectQuery(
    listProjects,
    {},
    {
      refetchInterval: 30000,
    }
  )

  const metrics = metricsResponse?.metrics
  const projects = projectsResponse?.projects
  const isLoading = metricsLoading || projectsLoading

  const stats = {
    totalProjects: metrics?.totalProjects ?? projects?.length ?? 0,
    totalApplications: metrics?.totalApplications ?? 0,
    healthyApplications: metrics?.healthyApplications ?? 0,
    syncedApplications: metrics?.syncedApplications ?? 0,
    outOfSyncApplications: metrics?.outOfSyncApplications ?? 0,
    degradedApplications: metrics?.degradedApplications ?? 0,
  }

  if (metricsError) {
    console.error('Failed to fetch metrics:', metricsError)
  }

  const healthPercent = stats.totalApplications > 0
    ? Math.round((stats.healthyApplications / stats.totalApplications) * 100)
    : 0

  const issueCount = stats.outOfSyncApplications + stats.degradedApplications

  return (
    <Flex direction="vertical" gap={24}>
      {/* Page Header */}
      <Flex direction="vertical" gap={4}>
        <Heading size="primary" as="h1">Dashboard</Heading>
        <Text color="light">Overview of your ArgoCD applications and projects</Text>
      </Flex>

      {/* Metrics Grid - aria-live for dynamic updates */}
      <section aria-label="Key metrics" aria-live="polite" aria-atomic="false">
        <Row gap={["m", "m"]} align="stretch">
          {/* Total Projects */}
          <Col span={3} sm={12} md={6} lg={3}>
            <Card style={{ height: '100%' }}>
              <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
                <Flex direction="horizontal" justify="space-between" align="center">
                  <Heading size="smallest" as="h2">Total Projects</Heading>
                  <Folder size={20} weight="bold" aria-hidden="true" />
                </Flex>
                {isLoading ? (
                  <Flex direction="vertical" gap={8}>
                    <Skeleton height={40} style={{ width: 80 }} />
                    <Skeleton height="sm" style={{ width: 100 }} />
                  </Flex>
                ) : (
                  <>
                    <Numeral
                      value={String(stats.totalProjects)}
                      size="n1"
                      data-testid="total-projects"
                      aria-label={`${stats.totalProjects} total projects`}
                    />
                    <Text size="p4" color="light">Active projects</Text>
                  </>
                )}
              </Flex>
            </Card>
          </Col>

          {/* Applications */}
          <Col span={3} sm={12} md={6} lg={3}>
            <Card style={{ height: '100%' }}>
              <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
                <Flex direction="horizontal" justify="space-between" align="center">
                  <Heading size="smallest" as="h2">Applications</Heading>
                  <Package size={20} weight="bold" aria-hidden="true" />
                </Flex>
                {isLoading ? (
                  <Flex direction="vertical" gap={8}>
                    <Skeleton height={40} style={{ width: 80 }} />
                    <Skeleton height="sm" style={{ width: 120 }} />
                  </Flex>
                ) : (
                  <>
                    <Numeral
                      value={String(stats.totalApplications)}
                      size="n1"
                      data-testid="total-applications"
                      aria-label={`${stats.totalApplications} applications`}
                    />
                    <Text size="p4" color="light">Managed by ArgoCD</Text>
                  </>
                )}
              </Flex>
            </Card>
          </Col>

          {/* Healthy - uses icon + text + color for status (not color alone) */}
          <Col span={3} sm={12} md={6} lg={3}>
            <Card accent="positive" style={{ height: '100%' }}>
              <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
                <Flex direction="horizontal" justify="space-between" align="center">
                  <Heading size="smallest" as="h2">
                    <Flex direction="horizontal" gap={8} align="center">
                      <Positive aria-hidden="true" />
                      Healthy
                    </Flex>
                  </Heading>
                </Flex>
                {isLoading ? (
                  <Flex direction="vertical" gap={8}>
                    <Skeleton height={40} style={{ width: 80 }} />
                    <Skeleton height="sm" style={{ width: 90 }} />
                  </Flex>
                ) : (
                  <>
                    <Numeral
                      value={String(stats.healthyApplications)}
                      size="n1"
                      data-testid="healthy-applications"
                      aria-label={`${stats.healthyApplications} healthy applications`}
                    />
                    <Text size="p4" color="light">
                      {stats.totalApplications > 0 ? `${healthPercent}% of total` : 'No applications'}
                    </Text>
                  </>
                )}
              </Flex>
            </Card>
          </Col>

          {/* Issues - uses icon + text + color for status (not color alone) */}
          <Col span={3} sm={12} md={6} lg={3}>
            <Link
              to="/issues"
              style={{ textDecoration: 'none', display: 'block', height: '100%' }}
              aria-label={`${issueCount} issues require attention. Click to view details.`}
            >
              <Card accent={issueCount > 0 ? 'negative' : undefined} style={{ cursor: 'pointer', height: '100%' }}>
                <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
                  <Flex direction="horizontal" justify="space-between" align="center">
                    <Heading size="smallest" as="h2">
                      <Flex direction="horizontal" gap={8} align="center">
                        <Warning size={18} weight="bold" aria-hidden="true" style={{ color: issueCount > 0 ? 'var(--status-negative)' : 'var(--text-secondary)' }} />
                        Issues
                      </Flex>
                    </Heading>
                    <ArrowRight size={16} weight="bold" aria-hidden="true" />
                  </Flex>
                  {isLoading ? (
                    <Flex direction="vertical" gap={8}>
                      <Skeleton height={40} style={{ width: 60 }} />
                      <Skeleton height="sm" style={{ width: 110 }} />
                    </Flex>
                  ) : (
                    <>
                      <Numeral
                        value={String(issueCount)}
                        size="n1"
                        aria-label={`${issueCount} issues`}
                      />
                      <Text size="p4" color="light">
                        {issueCount > 0 ? 'Require attention' : 'No issues'}
                      </Text>
                    </>
                  )}
                </Flex>
              </Card>
            </Link>
          </Col>
        </Row>
      </section>

      {/* Secondary Sections */}
      <Row gap={["m", "m"]} align="stretch">
        {/* Sync Status */}
        <Col span={6} sm={12} md={6}>
          <Card style={{ height: '100%', boxShadow: 'none' }}>
            <Flex direction="vertical" gap={16} style={{ padding: 16 }} role="region" aria-label="Sync status">
              <Flex direction="horizontal" gap={8} align="center">
                <ArrowsClockwise size={20} weight="bold" aria-hidden="true" />
                <Heading size="sub-section" as="h2">Sync status</Heading>
              </Flex>

              {isLoading ? (
                <Flex direction="vertical" gap={12} style={{ padding: 16 }}>
                  <Flex direction="horizontal" justify="space-between" align="center">
                    <Flex direction="horizontal" gap={8} align="center">
                      <Skeleton height={16} style={{ width: 16, borderRadius: '50%' }} />
                      <Skeleton height="sm" style={{ width: 60 }} />
                    </Flex>
                    <Flex direction="horizontal" gap={8} align="center">
                      <Skeleton height="sm" style={{ width: 128 }} />
                      <Skeleton height="sm" style={{ width: 32 }} />
                    </Flex>
                  </Flex>
                  <Flex direction="horizontal" justify="space-between" align="center">
                    <Flex direction="horizontal" gap={8} align="center">
                      <Skeleton height={16} style={{ width: 16, borderRadius: '50%' }} />
                      <Skeleton height="sm" style={{ width: 80 }} />
                    </Flex>
                    <Flex direction="horizontal" gap={8} align="center">
                      <Skeleton height="sm" style={{ width: 128 }} />
                      <Skeleton height="sm" style={{ width: 32 }} />
                    </Flex>
                  </Flex>
                </Flex>
              ) : stats.totalApplications === 0 ? (
                <Text color="light" style={{ textAlign: 'center', padding: 24 }}>
                  No applications yet
                </Text>
              ) : (
                <Flex direction="vertical" gap={16} role="list" aria-label="Sync status breakdown">
                  {/* Synced */}
                  <Flex
                    direction="horizontal"
                    justify="space-between"
                    align="center"
                    role="listitem"
                  >
                    <Flex direction="horizontal" gap={8} align="center">
                      <Positive aria-hidden="true" />
                      <Text size="p3">Synced</Text>
                    </Flex>
                    <Flex direction="horizontal" gap={8} align="center">
                      <ProgressBar
                        value={stats.syncedApplications / stats.totalApplications}
                        status="success"
                        size="small"
                        style={{ width: 128 }}
                        aria-label={`${stats.syncedApplications} of ${stats.totalApplications} synced`}
                      />
                      <Text size="p3" weight="semi-bold" monospace style={{ width: 32 }}>
                        {stats.syncedApplications}
                      </Text>
                    </Flex>
                  </Flex>

                  {/* Out of Sync */}
                  <Flex
                    direction="horizontal"
                    justify="space-between"
                    align="center"
                    role="listitem"
                  >
                    <Flex direction="horizontal" gap={8} align="center">
                      <Warning size={14} weight="bold" aria-hidden="true" />
                      <Text size="p3">Out of sync</Text>
                    </Flex>
                    <Flex direction="horizontal" gap={8} align="center">
                      <ProgressBar
                        value={stats.outOfSyncApplications / stats.totalApplications}
                        status="active"
                        size="small"
                        style={{ width: 128 }}
                        aria-label={`${stats.outOfSyncApplications} of ${stats.totalApplications} out of sync`}
                      />
                      <Text size="p3" weight="semi-bold" monospace style={{ width: 32 }}>
                        {stats.outOfSyncApplications}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Card>
        </Col>

        {/* Recent Projects */}
        <Col span={6} sm={12} md={6}>
          <Card style={{ height: '100%', boxShadow: 'none' }}>
            <Flex direction="vertical" gap={16} style={{ padding: 16 }} role="region" aria-label="Recent projects">
              <Flex direction="horizontal" gap={8} align="center">
                <Activity size={20} weight="bold" aria-hidden="true" />
                <Heading size="sub-section" as="h2">Recent projects</Heading>
              </Flex>

              {projectsLoading ? (
                <Flex direction="vertical" gap={12} style={{ padding: 8 }}>
                  {[...Array(5)].map((_, i) => (
                    <Flex key={i} direction="horizontal" justify="space-between" align="center">
                      <Flex direction="horizontal" gap={8} align="center">
                        <Skeleton height={16} style={{ width: 16, borderRadius: '50%' }} />
                        <Skeleton height="sm" style={{ width: 120 }} />
                      </Flex>
                      <Skeleton height={20} style={{ width: 40 }} />
                    </Flex>
                  ))}
                </Flex>
              ) : !projects || projects.length === 0 ? (
                <Text color="light" style={{ textAlign: 'center', padding: 24 }}>
                  No projects yet. Create one to get started!
                </Text>
              ) : (
                <Flex direction="vertical" gap={12} role="list" aria-label="List of recent projects">
                  {projects.slice(0, 5).map((project) => (
                    <Flex
                      key={project.id}
                      direction="horizontal"
                      justify="space-between"
                      align="center"
                      role="listitem"
                    >
                      <Flex direction="horizontal" gap={8} align="center">
                        <Folder size={16} weight="bold" aria-hidden="true" />
                        <Text size="p3" weight="semi-bold">{project.name}</Text>
                      </Flex>
                      <Flex direction="horizontal" gap={4} aria-label={project.tags?.length ? `Tags: ${project.tags.slice(0, 2).join(', ')}` : undefined}>
                        {project.tags?.slice(0, 2).map((tag) => (
                          <Tag key={tag} color="lavender">{tag}</Tag>
                        ))}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          </Card>
        </Col>
      </Row>
    </Flex>
  )
}
